import type { ReactElement } from 'react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BlockToggle } from '@/components/shared/BlockToggle';
import { formatRelativeTime } from '@/lib/date-utils';
import {
  HOOK_ENFORCED_SOURCES,
  formatList,
  splitEnforcedAssistants,
} from './endpointEnforcement';

interface McpEnforcementCellProps {
  row: MCPServerListItem;
  /** Owner/manager only; everyone else sees the switch disabled. */
  canEdit: boolean;
  pending: boolean;
  /** Allows the server; on a New, allowed row this reviews it ("Keep allowed"). */
  onAllow: (row: MCPServerListItem) => void;
  /** Opens the block confirmation; does not itself block. */
  onRequestBlock: (row: MCPServerListItem) => void;
}

/**
 * Enforcement cell for an Inventory MCP server discovered on endpoints
 * (KIR-4482): the same layout as the gateway's {@link GatewayControlCell} —
 * the shared Allowed/Blocked switch, then a status badge (the gateway's
 * "Applied"/"Pending sync" slot) that says a block is enforced by the
 * endpoint hook, which assistants it does not reach, and when the block was
 * decided. A server seen only in assistants without the hook gets a neutral
 * "Blocked where supported" badge instead. A New server that is still
 * allowed gets a "Keep allowed" action instead: allowing it is a review, so it
 * stops being New (KIR-4482 A2). Gateway rows carry their own control, so they
 * render nothing here.
 */
export function McpEnforcementCell({
  row,
  canEdit,
  pending,
  onAllow,
  onRequestBlock,
}: McpEnforcementCellProps): ReactElement | null {
  if (row.provenance === 'gateway') return null;
  const blocked = row.blocked ?? false;
  const blockedAt = row.blockUpdatedAt ?? null;
  // The hook only runs in some assistants; a server seen elsewhere still runs
  // there, so the badge must not read as "stopped" for it.
  const { notEnforced } = splitEnforcedAssistants(row.sources);
  const seenWhereEnforced =
    row.sources.length === 0 ||
    row.sources.some(s => HOOK_ENFORCED_SOURCES.includes(s));

  return (
    <div className="flex items-center gap-3">
      <BlockToggle
        blocked={blocked}
        name={row.name}
        onAllow={() => onAllow(row)}
        onRequestBlock={() => onRequestBlock(row)}
        disabled={!canEdit || pending}
      />
      {blocked && (
        <div className="flex flex-col items-start gap-0.5">
          {seenWhereEnforced ? (
            <Badge
              variant="outline"
              className="text-green-700 border-green-200"
            >
              Enforced by hook
            </Badge>
          ) : (
            <Badge variant="outline">Blocked where supported</Badge>
          )}
          {notEnforced.length > 0 && (
            <span className="text-xs text-muted-foreground">
              Not enforced in {formatList(notEnforced)}
            </span>
          )}
          {blockedAt && (
            <span className="text-xs text-muted-foreground">
              Blocked {formatRelativeTime(blockedAt)}
            </span>
          )}
        </div>
      )}
      {!blocked && row.isNew && canEdit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={pending}
          aria-label={`Keep ${row.name} allowed`}
          onClick={() => onAllow(row)}
        >
          Keep allowed
        </Button>
      )}
    </div>
  );
}
